import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
  ArchiveBoxArrowDownIcon
} from '@heroicons/react/24/outline';
import { patientService } from '../services/dataSource';
import { Patient } from '../types/patient';
import { useAuth } from '../hooks/useAuth';
import { useOrganizationStore } from '../stores/organizationStore';
import { toast } from '../utils/toast';
import { formatDate, formatDobWithAge } from '../utils/dateFormat';
import { useIsMobile } from '../hooks/useMediaQuery';
import { patientMrnLabel, patientName } from '../utils/patientDisplay';

const SEARCH_DEBOUNCE_MS = 300;

const Patients: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { operationMode } = useOrganizationStore();
  // Standalone lists patients by name and MRN (AMP-143, AMP-156); connected keeps the EHR's ID.
  const isStandalone = operationMode === 'standalone';
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState('');
  // The search the list was last asked for; trails the input by SEARCH_DEBOUNCE_MS.
  const [appliedSearch, setAppliedSearch] = useState('');
  const [isActive, setIsActive] = useState('true');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Check if user can create/edit patients and book appointments (only in standalone mode)
  const canModifyPatients = operationMode === 'standalone' && 
    (user?.role === 'admin' || user?.role === 'operator');
  // Why the add/edit/archive actions are unavailable, for tooltips and messages.
  const modifyBlockedReason = isStandalone
    ? 'Only admins and operators can add or change patients.'
    : 'Patients are managed in your EHR system in connected mode.';

  // Only the newest request may update the list; an older, slower response is dropped.
  const latestRequest = useRef(0);

  // A new search or status filter starts again from the first page.
  const goToFirstPage = () => setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }));

  useEffect(() => {
    if (searchTerm === appliedSearch) return;
    const timer = setTimeout(() => {
      setAppliedSearch(searchTerm);
      goToFirstPage();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, appliedSearch]);

  useEffect(() => {
    fetchPatients();
  }, [appliedSearch, pagination.page, isActive]);

  const fetchPatients = async () => {
    const requestId = ++latestRequest.current;
    try {
      setLoading(true);
      setError(null);
      
      const result = await patientService.getPatients({
        page: pagination.page,
        limit: pagination.limit,
        search: appliedSearch,
        isActive: isActive === 'all' ? undefined : isActive === 'true'
      });
      if (requestId !== latestRequest.current) return;

      setPatients(result.data.patients);
      setPagination(prev => ({
        ...prev,
        total: result.data.pagination.total,
        totalPages: result.data.pagination.pages
      }));
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch patients';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  const handleViewPatient = (patient: Patient) => {
    navigate(`/patients/${patient.id}`);
  };

  const handleEditPatient = (patient: Patient) => {
    if (!canModifyPatients) {
      toast.error(modifyBlockedReason);
      return;
    }
    navigate(`/patients/${patient.id}/edit`);
  };

  const handleCreatePatient = () => {
    if (!canModifyPatients) {
      toast.error(modifyBlockedReason);
      return;
    }
    navigate('/patients/new');
  };

  const handleArchivePatient = async (patient: Patient) => {
    if (!canModifyPatients) {
      toast.error(modifyBlockedReason);
      return;
    }

    if (!window.confirm(`Are you sure you want to archive ${patientName(patient)}? This will make them inactive but preserve their data.`)) {
      return;
    }

    try {
      await patientService.updatePatient(patient.id, { isActive: false });
      toast.success('Patient archived successfully');
      // Refresh the patient list
      fetchPatients();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to archive patient';
      toast.error(errorMessage);
    }
  };

  const handleUnarchivePatient = async (patient: Patient) => {
    if (!canModifyPatients) {
      toast.error(modifyBlockedReason);
      return;
    }

    try {
      await patientService.updatePatient(patient.id, { isActive: true });
      toast.success('Patient reactivated successfully');
      // Refresh the patient list
      fetchPatients();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reactivate patient';
      toast.error(errorMessage);
    }
  };


  const getGenderBadge = (gender: string) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full";
    switch (gender) {
      case 'male':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'female':
        return `${baseClasses} bg-pink-100 text-pink-800`;
      case 'other':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusBadge = (active: boolean) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full";
    return active 
      ? `${baseClasses} bg-green-100 text-green-800`
      : `${baseClasses} bg-gray-100 text-gray-800`;
  };


  return (
    <div className="space-y-6">
      {/* Header - Clinical Design */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-[var(--color-clinical-navy)]">Patients</h1>
          <p className="mt-1 text-sm text-[var(--color-clinical-blue)] opacity-80">
            Manage patient records and medical information
          </p>
        </div>
        <button
          className="btn-clinical-primary"
          onClick={handleCreatePatient}
          disabled={!canModifyPatients}
          title={!canModifyPatients ? modifyBlockedReason : undefined}
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Patient
        </button>
      </div>

      {/* Filters - Clinical Card */}
      <div className="clinical-card-static p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search patients..."
                className="input-field pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="sm:w-48">
            <select
              className="input-field"
              value={isActive}
              onChange={(e) => {
                setIsActive(e.target.value);
                goToFirstPage();
              }}
            >
              <option value="all">All Patients</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Connected Mode Warning - Clinical styling */}
      {operationMode === 'connected' && (
        <div className="clinical-card-static p-4 bg-[rgba(241,196,15,0.08)] border border-[rgba(241,196,15,0.3)]">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-[var(--color-accent-amber)] mr-3" />
            <div>
              <p className="text-sm text-[var(--color-clinical-navy)]">
                <strong>Connected Mode:</strong> Patient data is read-only and synchronized from your EHR system.
                To modify patient information, please use your EHR system.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="card p-8">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600">Loading patients...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="card p-6 bg-red-50 border border-red-200">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-red-800">Error loading patients</p>
              <p className="text-sm text-red-600 mt-1">{error}</p>
              <button 
                onClick={fetchPatients} 
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patients List */}
      {!loading && !error && (
        <div className="clinical-card-static">
          <div className="p-6 border-b border-[rgba(10,31,68,0.08)]">
            <h3 className="text-lg font-display font-semibold text-[var(--color-clinical-navy)]">
              Patients ({pagination.total})
            </h3>
          </div>

          {/* Mobile Card View */}
          {isMobile ? (
            <div className="divide-y divide-[rgba(10,31,68,0.08)]">
              {patients?.map((patient) => (
                <div
                  key={patient.id}
                  className="p-4 hover:bg-[rgba(10,31,68,0.02)] transition-colors"
                  onClick={() => handleViewPatient(patient)}
                >
                  {/* Header: name, MRN and status */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-base font-semibold text-[var(--color-clinical-navy)]">
                        {isStandalone ? patientName(patient) : patient.patientId}
                      </div>
                      {formatDobWithAge(patient.demographics.dateOfBirth) && (
                        <div className="text-sm text-[var(--color-clinical-navy)] mt-0.5">
                          DOB: {formatDobWithAge(patient.demographics.dateOfBirth)}
                        </div>
                      )}
                      <div className="text-xs text-[var(--color-clinical-blue)] opacity-70 mt-0.5">
                        {isStandalone && `${patientMrnLabel(patient)} · `}Created: {formatDate(patient.createdAt)}
                      </div>
                    </div>
                    <span className={getStatusBadge(patient.isActive)}>
                      {patient.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Demographics */}
                  <div className="flex items-center gap-2 mb-3">
                    {patient.demographics.gender && (
                      <span className={getGenderBadge(patient.demographics.gender)}>
                        {patient.demographics.gender}
                      </span>
                    )}
                    {patient.demographics.language && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {patient.demographics.language.toUpperCase()}
                      </span>
                    )}
                    <span className="text-xs text-gray-500 ml-auto">
                      Updated: {formatDate(patient.updatedAt)}
                    </span>
                  </div>

                  {/* Medical Info */}
                  {(patient.medicalRecord.conditions?.length > 0 || patient.medicalRecord.allergies?.length > 0) && (
                    <div className="space-y-1 mb-3 text-sm">
                      {patient.medicalRecord.conditions && patient.medicalRecord.conditions.length > 0 && (
                        <div className="text-gray-700">
                          <span className="font-medium">Conditions:</span>{' '}
                          {patient.medicalRecord.conditions.slice(0, 2).join(', ')}
                          {patient.medicalRecord.conditions.length > 2 && '...'}
                        </div>
                      )}
                      {patient.medicalRecord.allergies && patient.medicalRecord.allergies.length > 0 && (
                        <div className="text-red-600">
                          <span className="font-medium">Allergies:</span>{' '}
                          {patient.medicalRecord.allergies.slice(0, 2).join(', ')}
                          {patient.medicalRecord.allergies.length > 2 && '...'}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons - Touch friendly */}
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[rgba(10,31,68,0.06)]">
                    <button
                      className="flex items-center gap-1.5 px-3 py-2 min-h-11 text-sm font-medium text-[var(--color-surgical-teal)] bg-[rgba(22,160,133,0.1)] rounded-lg hover:bg-[rgba(22,160,133,0.2)] transition-colors"
                      onClick={(e) => { e.stopPropagation(); handleViewPatient(patient); }}
                      aria-label="View patient details"
                    >
                      <EyeIcon className="w-4 h-4" />
                      View
                    </button>
                    {canModifyPatients && (
                      <button
                        className="flex items-center gap-1.5 px-3 py-2 min-h-11 text-sm font-medium text-[var(--color-clinical-blue)] bg-[rgba(30,74,122,0.1)] rounded-lg hover:bg-[rgba(30,74,122,0.2)] transition-colors"
                        onClick={(e) => { e.stopPropagation(); handleEditPatient(patient); }}
                        aria-label="Edit patient"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Edit
                      </button>
                    )}
                    {canModifyPatients && patient.isActive && (
                      <button
                        className="flex items-center gap-1.5 px-3 py-2 min-h-11 text-sm font-medium text-white bg-[var(--color-surgical-teal)] rounded-lg hover:opacity-90 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); navigate(`/appointments/new?patientId=${patient.id}`); }}
                        aria-label="Schedule new appointment"
                      >
                        + Appointment
                      </button>
                    )}
                    {canModifyPatients && (
                      patient.isActive ? (
                        <button
                          className="flex items-center gap-1.5 px-3 py-2 min-h-11 text-sm font-medium text-[var(--color-accent-amber)] bg-[rgba(241,196,15,0.1)] rounded-lg hover:bg-[rgba(241,196,15,0.2)] transition-colors"
                          onClick={(e) => { e.stopPropagation(); handleArchivePatient(patient); }}
                          aria-label="Archive patient"
                        >
                          <ArchiveBoxIcon className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          className="flex items-center gap-1.5 px-3 py-2 min-h-11 text-sm font-medium text-[var(--color-surgical-teal)] bg-[rgba(22,160,133,0.1)] rounded-lg hover:bg-[rgba(22,160,133,0.2)] transition-colors"
                          onClick={(e) => { e.stopPropagation(); handleUnarchivePatient(patient); }}
                          aria-label="Reactivate patient"
                        >
                          <ArchiveBoxArrowDownIcon className="w-4 h-4" />
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Desktop Table View */
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[rgba(10,31,68,0.08)]">
                <thead className="bg-[rgba(10,31,68,0.02)]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[var(--color-clinical-blue)] uppercase tracking-wider">
                      {isStandalone ? 'Patient' : 'Patient ID'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[var(--color-clinical-blue)] uppercase tracking-wider">
                      Demographics
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[var(--color-clinical-blue)] uppercase tracking-wider">
                      Medical Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[var(--color-clinical-blue)] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[var(--color-clinical-blue)] uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[var(--color-clinical-blue)] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/50 divide-y divide-[rgba(10,31,68,0.06)]">
                  {patients?.map((patient) => (
                    <tr key={patient.id} className="hover:bg-[rgba(10,31,68,0.02)] transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-[var(--color-clinical-navy)]">
                          {isStandalone ? patientName(patient) : patient.patientId}
                        </div>
                        <div className="text-sm text-[var(--color-clinical-blue)] opacity-70">
                          {isStandalone && `${patientMrnLabel(patient)} · `}Created: {formatDate(patient.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {patient.demographics.gender && (
                            <span className={getGenderBadge(patient.demographics.gender)}>
                              {patient.demographics.gender}
                            </span>
                          )}
                          {patient.demographics.language && (
                            <span className="text-sm text-gray-500">
                              {patient.demographics.language.toUpperCase()}
                            </span>
                          )}
                        </div>
                        {formatDobWithAge(patient.demographics.dateOfBirth) && (
                          <div className="text-sm text-gray-900 mt-1">
                            DOB: {formatDobWithAge(patient.demographics.dateOfBirth)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {patient.medicalRecord.conditions && patient.medicalRecord.conditions.length > 0 && (
                            <div>
                              <span className="text-xs font-medium text-gray-500">Conditions:</span>
                              <div className="text-sm text-gray-900">
                                {patient.medicalRecord.conditions.slice(0, 2).join(', ')}
                                {patient.medicalRecord.conditions.length > 2 && '...'}
                              </div>
                            </div>
                          )}
                          {patient.medicalRecord.allergies && patient.medicalRecord.allergies.length > 0 && (
                            <div>
                              <span className="text-xs font-medium text-gray-500">Allergies:</span>
                              <div className="text-sm text-red-600">
                                {patient.medicalRecord.allergies.slice(0, 2).join(', ')}
                                {patient.medicalRecord.allergies.length > 2 && '...'}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(patient.isActive)}>
                          {patient.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(patient.updatedAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            className="text-[var(--color-surgical-teal)] hover:text-[var(--color-clinical-blue)] transition-colors"
                            onClick={() => handleViewPatient(patient)}
                            title="View patient details"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <button
                            className={`${canModifyPatients ? 'text-[var(--color-clinical-blue)] hover:text-[var(--color-clinical-navy)] transition-colors' : 'text-gray-400 cursor-not-allowed'}`}
                            onClick={() => handleEditPatient(patient)}
                            disabled={!canModifyPatients}
                            title={canModifyPatients ? 'Edit patient' : modifyBlockedReason}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          {canModifyPatients && patient.isActive && (
                            <button
                              className="text-[var(--color-cyan-bright)] hover:text-[var(--color-surgical-teal)] transition-colors text-xs font-semibold"
                              onClick={() => navigate(`/appointments/new?patientId=${patient.id}`)}
                              title="Schedule new appointment"
                            >
                              New Appointment
                            </button>
                          )}
                          {canModifyPatients && (
                            patient.isActive ? (
                              <button
                                className="text-[var(--color-accent-amber)] hover:text-amber-700 transition-colors"
                                onClick={() => handleArchivePatient(patient)}
                                title="Archive patient (make inactive)"
                              >
                                <ArchiveBoxIcon className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                className="text-[var(--color-surgical-teal)] hover:text-emerald-700 transition-colors"
                                onClick={() => handleUnarchivePatient(patient)}
                                title="Reactivate patient"
                              >
                                <ArchiveBoxArrowDownIcon className="w-4 h-4" />
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!patients || patients.length === 0) && !loading && (
            <div className="text-center py-12">
              <UserGroupIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-display font-semibold text-[var(--color-clinical-navy)]">No patients found</h3>
              <p className="mt-1 text-sm text-[var(--color-clinical-blue)] opacity-70">
                {appliedSearch
                  ? 'Try adjusting your search criteria.'
                  : operationMode === 'connected'
                  ? 'Patient data will be synchronized from your EHR system.'
                  : 'Get started by adding a new patient.'
                }
              </p>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-4 sm:px-6 py-4 border-t border-[rgba(10,31,68,0.08)]">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-sm text-[var(--color-clinical-blue)] text-center sm:text-left">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} patients
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 min-h-11 text-sm font-medium border border-[rgba(10,31,68,0.15)] rounded-lg text-[var(--color-clinical-navy)] hover:bg-[rgba(10,31,68,0.04)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="px-4 py-2 min-h-11 text-sm font-medium border border-[rgba(10,31,68,0.15)] rounded-lg text-[var(--color-clinical-navy)] hover:bg-[rgba(10,31,68,0.04)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* HIPAA Notice */}
      <div className="clinical-card-static p-4 bg-[rgba(30,74,122,0.04)] border border-[rgba(30,74,122,0.15)]">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-2 h-2 bg-[var(--color-surgical-teal)] rounded-full animate-pulse"></div>
          </div>
          <div className="ml-3">
            <p className="text-sm text-[var(--color-clinical-navy)]">
              <strong>HIPAA Notice:</strong> All patient information is encrypted and stored securely.
              Access is logged and monitored for compliance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Patients;